import { Contact } from '@modules/contacts/shared/models/contact.model';
import { UntypedFormGroup } from '@angular/forms';
import {
  Component,
  Input,
  EventEmitter,
  ViewChild,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  input,
  effect
} from '@angular/core';
import { SnackBarService } from '@core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { Config } from '@shared/configs/config';
import { Regex } from '@shared/configs/regex';
import { NgSelectComponent } from '@ng-select/ng-select';
import { AssignedContactList } from '@shared/models/assigned-contact.model';
import { TaskSidenavService } from '@shared/modules/task-sidenav/services/task-sidenav.service';
import { StorageService } from '@core/services/storage.service';
import { TaskService } from '@shared/modules/tasks/services/task-service';
import { take, takeUntil, tap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ListTaskService } from '@shared/modules/list/services/list-task.service';
import { HttpParams } from '@angular/common/http';

@Component({
  selector: 'employee-search',
  templateUrl: './employee-search.component.html',
  styleUrls: ['./employee-search.component.scss']
})
export class EmployeeSearchComponent implements OnInit, OnDestroy {
  @Input() controlName: string = 'assignedToIds';
  @Input() initialValue: any[] = [];
  @Input() form: UntypedFormGroup;
  @Input() projectId: string = null;
  @Input() requiredid: number = JSON.parse(this.store.get('employee')).contacts[0].id;
  @Input() readonly: boolean = false;
  @Input() assignMeOption: boolean = true;

  onlyEmployees = input<boolean>(false);
  requiredIds = input<number[]>([]);
  bindIdField = input<boolean>(false);
  ignoreCurrentUserId = input<boolean>(false);
  addFn = input<Function>(null);
  tabIndex = input<number>(-1);

  currentUserId = JSON.parse(this.store.get('employee')).contacts[0].id;
  currentUser = JSON.parse(this.store.get('employee'));

  items: Contact[] = [];
  destroy$ = new Subject<void>();

  @ViewChild('select') select: NgSelectComponent;

  addNewPerson: EventEmitter<any> = new EventEmitter();

  list: AssignedContactList = new AssignedContactList();

  loading: boolean = false;
  private isFirstRun = true;

  get queryParams(): string {
    let params = new HttpParams();

    if (this.projectId) params = params.set('projectId', this.projectId);
    if (this.onlyEmployees()) params = params.set('onlyEmployees', this.onlyEmployees());
    this.requiredIds()?.forEach((id) => {
      if (id) params = params.append('requiredIds[]', id);
    });

    return params.toString();
  }

  get URL(): string {
    let params = new HttpParams({ fromString: this.queryParams });
    if (!this.ignoreCurrentUserId()) params = params.append('requiredIds[]', this.requiredid);
    return `${Config.API}/contact/task-assign?${params.toString()}`;
  }

  get assignedEmails() {
    const items =
      this.select?.selectedItems
        .filter((i) => !!(i.value as any)?.isRecentlyCreated)
        .map((i) => (i.value as any)?.email) || [];
    return items;
  }

  get assignedIds() {
    const items =
      this.select?.selectedItems
        .filter((i) => !(i.value as any)?.isRecentlyCreated)
        .map((i) => (i.value as any)?.id) || [];
    return items;
  }

  get formData() {
    return { assignedToIds: this.assignedIds, assignedEmails: this.assignedEmails };
  }

  get isCurrentUserAssigned(): boolean {
    const assigned = this.form.get(this.controlName)?.value || [];
    return assigned.includes(this.currentUser.id);
  }

  constructor(
    private s: SnackBarService,
    private t: TranslateService,
    public taskSidenavService: TaskSidenavService,
    private store: StorageService,
    private taskService: TaskService,
    private cdr: ChangeDetectorRef,
    public listTaskService: ListTaskService
  ) {
    effect(() => {
      this.requiredIds();

      if (this.isFirstRun) {
        this.isFirstRun = false;
        return;
      }

      this.select?.filter('');
      this.select?.close();
      this.select?.blur();
    });
  }

  ngOnInit(): void {
    this.setFormPristine();
    this.setSubscriptions();
  }

  setSubscriptions(): void {
    this.taskSidenavService.formSubmitted
      .pipe(
        tap((submitted: boolean) => {
          if (submitted) {
            this.form.get(this.controlName).markAsTouched();
            this.cdr.detectChanges();
          }
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  setFormPristine(): void {
    this.form.get(this.controlName).markAsPristine();
    this.form.get(this.controlName).markAsUntouched();
    if (!this.listTaskService.isMyWorkTaskLists) {
      this.form.get(this.controlName).setValidators([]);
    }
    this.cdr.detectChanges();
  }

  createNewPerson(person: { email: string }, items: Array<any>): void {
    const { email } = person;
    const user = this.list.createCustomTag(email);
    this.addNewPerson.emit([user]);
  }

  changePromise($event) {
    const contact = $event?.value?.object;
    if (!contact?.employee) {
      return this.delegatePromise();
    }
    return true;
  }

  addTagPromise(email: string) {
    return this.delegatePromise();
  }

  delegatePromise() {
    return new Promise((resolve, reject) => {
      const accepted = confirm(this.t.instant('Mailings.Templates.taskDelegation'));
      accepted ? resolve(true) : reject('Cannot delegate task!');
    });
  }

  async validateEmail(email: string) {
    if (!!email?.length && !Regex.email.test(email.toString())) {
      this.s.error(this.t.instant('FormErrors.email'));
      return null;
    }

    if (this.addTagPromise) {
      const canAdd = await this.addTagPromise(email);
      canAdd ? this.list.createCustomTag(email) : false;
    }
    return this.list.createCustomTag(email);
  }

  transformEmployees(list: Contact[]) {
    this.items = list;
    this.list.updateList(list);
    if (this.bindIdField()) this.list.list = this.list.list.map((i) => ({ ...i, id: i.object?.id }));
    return this.list.list;
  }

  toggleAssignMe(): void {
    const me = JSON.parse(this.store.get('employee'));
    const control = this.form.get(this.controlName);
    const current = control?.value || [];

    if (this.isCurrentUserAssigned) {
      const updated = current.filter((id: number) => id !== me.id);
      control?.setValue(updated);
      this.select.detectChanges();
    } else {
      if (this.taskSidenavService.task?.id) {
        this.taskService
          .assignTaskToMe(this.taskSidenavService.task.id)
          .pipe(
            take(1),
            tap(() => {
              const updated = Array.from(new Set([...current, me.id]));
              control?.setValue(updated);
              this.select.detectChanges();
            })
          )
          .subscribe();
      } else {
        const updated = Array.from(new Set([...current, me.id]));
        control?.setValue(updated);
        this.select.detectChanges();
      }
    }
  }

  ngOnDestroy(): void {
    this.taskSidenavService.formSubmitted.next(false);
    this.setFormPristine();
    this.form.get(this.controlName).reset();
    this.form.get(this.controlName).setErrors(null);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
